export interface AccessContext {
  isSecurityAdministrator: boolean;
  hasFullApplicationAccess: boolean;
  permissions: Record<string, string[]>;
  applicationRoleIds: string[];
}

const permissionNamespacePattern = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/;
const permissionActionPattern = /^[a-z0-9]+(?:[.-][a-z0-9]+)*$/;

function isValidPermissionGroup(
  group: string,
  actions: unknown,
): actions is string[] {
  return (
    group.length <= 126 &&
    permissionNamespacePattern.test(group) &&
    Array.isArray(actions) &&
    actions.every(
      (action: unknown) =>
        typeof action === 'string' &&
        permissionActionPattern.test(action) &&
        group.length + action.length + 1 <= 128,
    )
  );
}

export function parseAccessContext(value: unknown): AccessContext {
  if (
    !isRecord(value) ||
    typeof value['isSecurityAdministrator'] !== 'boolean' ||
    typeof value['hasFullApplicationAccess'] !== 'boolean' ||
    !isRecord(value['permissions']) ||
    !Array.isArray(value['applicationRoleIds']) ||
    value['applicationRoleIds'].some(
      (role: unknown) =>
        typeof role !== 'string' ||
        !role.trim() ||
        role.length > 255 ||
        hasControlCharacter(role),
    )
  ) {
    throw new Error('Invalid access context');
  }
  const permissions: Record<string, string[]> = {};
  for (const [group, actions] of Object.entries(value['permissions'])) {
    if (!isValidPermissionGroup(group, actions)) {
      throw new Error('Invalid access permissions');
    }
    permissions[group] = actions;
  }
  return {
    isSecurityAdministrator: value['isSecurityAdministrator'],
    hasFullApplicationAccess: value['hasFullApplicationAccess'],
    permissions,
    applicationRoleIds: value['applicationRoleIds'],
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasControlCharacter(value: string): boolean {
  for (let index = 0; index < value.length; index++) {
    const code = value.charCodeAt(index);
    if (code <= 0x1f || (code >= 0x7f && code <= 0x9f)) return true;
  }
  return false;
}
